import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { validateAge, getAgeValidationMessage, logAgeValidation, logConsent } from '../../utils/ageValidation';
import { getDeviceFingerprint } from '../../lib/fingerprint';
import { trackRegistrationComplete } from '../../lib/analytics';
import { Spinner } from '../ui/Spinner';
import { Link } from 'react-router-dom';
import { MapGeneratingLoader } from '../onboarding/MapGeneratingLoader';
import { trackOnboardingEvent } from '../../lib/analytics-events';
import { BirthDateInput } from '../ui/BirthDateInput';
import { BirthTimeInput } from '../ui/BirthTimeInput';
import { CityAutocomplete } from '../ui/CityAutocomplete';

interface RegisterStep2Props {
  userId: string;
  email: string;
  onBack: () => void;
}

export function RegisterStep2({ userId, email, onBack }: RegisterStep2Props) {
  const navigate = useNavigate();

  const [name,             setName]             = useState('');
  const [birthDate,        setBirthDate]        = useState('');
  const [birthTime,        setBirthTime]        = useState('');
  const [timeUnknown,      setTimeUnknown]      = useState(false);
  const [birthCity,        setBirthCity]        = useState('');
  const [ageError,         setAgeError]         = useState<string | null>(null);
  const [consentTerms,     setConsentTerms]     = useState(false);
  const [consentPrivacy,   setConsentPrivacy]   = useState(false);
  const [consentCancel,    setConsentCancel]    = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(true);
  const [consentAlerts,    setConsentAlerts]    = useState(true);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState<string | null>(null);
  const [generatingChart,  setGeneratingChart]  = useState(false);

  // Track step 2 start once
  useState(() => {
    trackOnboardingEvent('step2_birthdata_started', { email });
  });

  const ageOk     = birthDate ? validateAge(birthDate).isValid : false;
  const canSubmit = name.trim().length > 1 && birthDate && ageOk
    && consentTerms && consentPrivacy && consentCancel && !loading;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const ageResult = validateAge(birthDate);
    if (!ageResult.isValid) {
      setAgeError(getAgeValidationMessage(ageResult.reason));
      logAgeValidation({ userId, birthDate, age: ageResult.age, result: `blocked_${ageResult.reason}` as never, context: 'registration' });
      return;
    }

    setLoading(true);
    setError(null);

    const now = new Date().toISOString();

    const { error: profileError } = await supabase.from('profiles').update({
      full_name:                    name.trim(),
      birth_date:                   birthDate,
      birth_time:                   timeUnknown ? null : (birthTime || null),
      birth_city:                   birthCity.trim() || null,
      birth_date_registration:      birthDate,
      age_validation_passed:        true,
      age_last_validated_at:        now,
      marketing_email_consent:      consentMarketing,
      marketing_email_consented_at: consentMarketing ? now : null,
      platform_alerts_consent:      consentAlerts,
      platform_alerts_consented_at: now,
    }).eq('id', userId);

    if (profileError) {
      setError('Erro ao salvar perfil. Tente novamente.');
      setLoading(false);
      return;
    }

    logAgeValidation({ userId, birthDate, age: ageResult.age, result: 'passed', context: 'registration' });
    (['terms_of_use', 'privacy_policy', 'cancellation_policy'] as const).forEach(type => {
      logConsent({ userId, consentType: type, action: 'granted', context: 'registration' });
    });
    if (consentMarketing) logConsent({ userId, consentType: 'marketing_email', action: 'granted', context: 'registration' });
    logConsent({ userId, consentType: 'platform_alerts', action: consentAlerts ? 'granted' : 'revoked', context: 'registration' });

    trackRegistrationComplete();
    trackOnboardingEvent('step2_birthdata_completed', { email, metadata: { has_birth_time: !timeUnknown && !!birthTime } });
    trackOnboardingEvent('map_generation_started', { email });

    // RT-19: multi-account fingerprint check (silent, fire-and-forget)
    getDeviceFingerprint().then(async fp => {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86_400_000).toISOString();
      const { count } = await supabase.from('profiles').select('*', { count: 'exact', head: true })
        .eq('device_fingerprint', fp).gte('created_at', thirtyDaysAgo);
      if ((count ?? 0) > 3) {
        await supabase.from('profiles').update({ suspicious: true, suspicious_reason: 'device_fingerprint_limit' }).eq('id', userId);
      }
    }).catch(() => {});

    setGeneratingChart(true);
    navigate('/onboarding', { replace: true });
  }

  if (generatingChart) {
    return (
      <div className="card">
        <MapGeneratingLoader />
      </div>
    );
  }

  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-28)', color: 'var(--bg)', margin: '0 0 0.35rem' }}>
          Seus dados de nascimento
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
          Precisamos disso para calcular seu mapa natal preciso
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label htmlFor="reg-name" style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
            Nome completo
          </label>
          <input id="reg-name" type="text" className="form-input" value={name} onChange={e => setName(e.target.value)}
            placeholder="Seu nome completo" required style={{ width: '100%' }} />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
            Data de nascimento
          </label>
          <BirthDateInput value={birthDate} onChange={v => { setBirthDate(v); setAgeError(null); }} required />
          {ageError && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', margin: '0.3rem 0 0' }}>{ageError}</p>}
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
            Hora de nascimento <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(recomendado)</span>
          </label>
          <BirthTimeInput value={birthTime} onChange={setBirthTime} disabled={timeUnknown} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={timeUnknown} onChange={e => setTimeUnknown(e.target.checked)} style={{ accentColor: 'var(--petrol)' }} />
            Não sei a hora exata
          </label>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
            ℹ Sem a hora, não conseguimos calcular seu Ascendente.
          </p>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 'var(--text-label)', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
            Local de nascimento
          </label>
          <CityAutocomplete value={birthCity} onChange={setBirthCity} placeholder="Cidade, Estado, País" />
        </div>

        {/* LGPD consent checkboxes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.875rem', borderRadius: 'var(--radius-sm)', background: 'var(--bg-elevated)' }}>
          {[
            { state: consentTerms,   setter: setConsentTerms,   label: <>Aceito os <Link to="/termos" style={{ color: 'var(--petrol)' }}>Termos de Uso</Link></> },
            { state: consentPrivacy, setter: setConsentPrivacy, label: <>Li e aceito a <Link to="/privacidade" style={{ color: 'var(--petrol)' }}>Política de Privacidade</Link></> },
            { state: consentCancel,  setter: setConsentCancel,  label: <>Entendo a <Link to="/cancelamento" style={{ color: 'var(--petrol)' }}>Política de Cancelamento</Link></> },
          ].map(({ state, setter, label }, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={state} onChange={e => setter(e.target.checked)} required style={{ accentColor: 'var(--petrol)', marginTop: '1px', flexShrink: 0 }} />
              {label}
            </label>
          ))}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={consentMarketing} onChange={e => setConsentMarketing(e.target.checked)} style={{ accentColor: 'var(--petrol)', marginTop: '1px', flexShrink: 0 }} />
            Aceito receber e-mails com dicas e promoções (opcional)
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={consentAlerts} onChange={e => setConsentAlerts(e.target.checked)} style={{ accentColor: 'var(--petrol)', marginTop: '1px', flexShrink: 0 }} />
            Aceito receber alertas e notificações da plataforma (opcional)
          </label>
        </div>

        {error && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', margin: 0 }}>⚠ {error}</p>}

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button type="button" className="btn btn-ghost" onClick={onBack} style={{ flex: '0 0 auto' }}>
            ← Voltar
          </button>
          <button type="submit" className="btn btn-primary" disabled={!canSubmit} style={{ flex: 1 }}>
            {loading ? <Spinner size="sm" /> : 'Gerar Meu Mapa Natal ✦'}
          </button>
        </div>

        <p style={{ textAlign: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
          Conta criada para <strong style={{ color: 'var(--text-secondary)' }}>{email}</strong>
        </p>
      </form>
    </>
  );
}
